/**
 * Canvas renderer component.
 * Recursively renders component trees from JSON definitions.
 *
 * Tasks: T031 (recursive rendering), T032 (error boundary), T033 (unknown fallback), T042 (flicker prevention)
 */

import React, { Component, Suspense, memo, useMemo, useRef, useDeferredValue } from 'react';
import { AlertTriangle, HelpCircle, Loader2 } from 'lucide-react';
import type { ComponentTree, ComponentType, VisibilityCondition } from '../../types/canvas';
import { MAX_NESTING_DEPTH } from '../../types/canvas';
import { canvasCatalog, validateTree } from './catalog';

// ============================================================================
// Loading Fallback for Lazy Components (T072)
// ============================================================================

function LazyLoadingFallback({ componentType }: { componentType: string }) {
  return (
    <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg border border-gray-200">
      <Loader2 className="w-5 h-5 animate-spin text-gray-400 mr-2" />
      <span className="text-sm text-gray-500">Loading {componentType}...</span>
    </div>
  );
}

// ============================================================================
// Tree Stability Helpers (T042)
// ============================================================================

/**
 * Generate a stable hash for a component tree to detect meaningful changes.
 * This prevents unnecessary re-renders during rapid updates.
 */
function hashTree(tree: ComponentTree): string {
  // Fast shallow hash based on type and key props
  const parts = [tree.type];
  if (tree.props) {
    // Include stringified props for comparison (limited depth)
    try {
      parts.push(JSON.stringify(tree.props));
    } catch {
      // Fallback if circular reference
      parts.push(Object.keys(tree.props).join(','));
    }
  }
  if (tree.children?.length) {
    parts.push(String(tree.children.length));
  }
  return parts.join('|');
}

// ============================================================================
// Error Boundary (T032)
// ============================================================================

interface ErrorBoundaryProps {
  componentType: string;
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ComponentErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(`Canvas component error in ${this.props.componentType}:`, error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-center gap-2 text-red-700">
            <AlertTriangle size={16} />
            <span className="text-sm font-medium">
              Error rendering {this.props.componentType}
            </span>
          </div>
          <p className="mt-1 text-xs text-red-600">
            {this.state.error?.message || 'An unexpected error occurred'}
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}

// ============================================================================
// Unknown Type Fallback (T033)
// ============================================================================

interface UnknownComponentProps {
  type: string;
  props: Record<string, unknown>;
}

function UnknownComponent({ type, props }: UnknownComponentProps) {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
      <div className="flex items-center gap-2 text-gray-600">
        <HelpCircle size={16} />
        <span className="text-sm font-medium">Unknown component: {type}</span>
      </div>
      <details className="mt-2">
        <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
          View props
        </summary>
        <pre className="mt-1 text-xs text-gray-600 bg-gray-100 p-2 rounded overflow-auto max-h-32">
          {JSON.stringify(props, null, 2)}
        </pre>
      </details>
    </div>
  );
}

// ============================================================================
// Visibility Evaluation
// ============================================================================

interface ContextData {
  [key: string]: unknown;
}

function getValueByPath(obj: ContextData, path: string): unknown {
  const parts = path.split('.');
  let current: unknown = obj;

  for (const part of parts) {
    if (current === null || current === undefined) {
      return undefined;
    }
    if (typeof current === 'object') {
      current = (current as Record<string, unknown>)[part];
    } else {
      return undefined;
    }
  }

  return current;
}

function evaluateVisibility(
  condition: VisibilityCondition | undefined,
  context: ContextData
): boolean {
  if (condition === undefined) return true;
  if (typeof condition === 'boolean') return condition;

  if ('path' in condition) {
    const value = getValueByPath(context, condition.path);
    if ('equals' in condition) {
      return value === condition.equals;
    }
    // Default: check if value is truthy
    return Boolean(value);
  }

  if ('and' in condition) {
    return condition.and.every((c) => evaluateVisibility(c, context));
  }

  if ('or' in condition) {
    return condition.or.some((c) => evaluateVisibility(c, context));
  }

  if ('not' in condition) {
    return !evaluateVisibility(condition.not, context);
  }

  return true;
}

// ============================================================================
// Recursive Component Renderer (T031)
// ============================================================================

interface ComponentRendererProps {
  node: ComponentTree;
  depth: number;
  context: ContextData;
  onAction?: ActionHandler;
}

// T053: Component types that support action handlers
const INTERACTIVE_COMPONENTS = new Set(['Form', 'Button']);

function ComponentRendererInner({ node, depth, context, onAction }: ComponentRendererProps) {
  // Check nesting depth
  if (depth > MAX_NESTING_DEPTH) {
    return (
      <div className="text-red-500 text-sm p-2 bg-red-50 rounded">
        Maximum nesting depth ({MAX_NESTING_DEPTH}) exceeded
      </div>
    );
  }

  // Evaluate visibility
  if (!evaluateVisibility(node.visible, context)) {
    return null;
  }

  const catalogEntry = canvasCatalog[node.type as ComponentType];

  // Handle unknown component type
  if (!catalogEntry || !catalogEntry.component) {
    return <UnknownComponent type={node.type} props={node.props} />;
  }

  // Cast to a component type that accepts children
  const Component = catalogEntry.component as React.ComponentType<
    Record<string, unknown> & { children?: React.ReactNode }
  >;
  const isLazy = catalogEntry.isLazy ?? false;

  // Render children recursively if component accepts them
  const renderedChildren = catalogEntry.hasChildren && node.children?.length ? (
    <>
      {node.children.map((child, index) => (
        <ComponentRenderer
          key={index}
          node={child}
          depth={depth + 1}
          context={context}
          onAction={onAction}
        />
      ))}
    </>
  ) : undefined;

  // T053: Pass onAction to interactive components
  const componentProps = INTERACTIVE_COMPONENTS.has(node.type)
    ? { ...node.props, onAction }
    : node.props;

  // T072: Wrap lazy components in Suspense
  const componentElement = (
    <Component {...componentProps}>{renderedChildren}</Component>
  );

  return (
    <ComponentErrorBoundary componentType={node.type}>
      {isLazy ? (
        <Suspense fallback={<LazyLoadingFallback componentType={node.type} />}>
          {componentElement}
        </Suspense>
      ) : (
        componentElement
      )}
    </ComponentErrorBoundary>
  );
}

const ComponentRenderer = memo(ComponentRendererInner);

// ============================================================================
// Canvas Renderer
// ============================================================================

// T053: Action handler type for interactive components
export type ActionHandler = (action: { name: string; params?: Record<string, unknown> }) => Promise<void>;

export interface CanvasRendererProps {
  tree: ComponentTree;
  context?: ContextData;
  className?: string;
  /** T053: Action handler for interactive components (Form, Button) */
  onAction?: ActionHandler;
  /** T059: Error message to display with retry option */
  error?: string;
  /** T059: Retry callback when user clicks retry button */
  onRetry?: () => void;
}

function CanvasRendererBase({ tree, context = {}, className, onAction, error, onRetry }: CanvasRendererProps) {
  // T042: Use deferred value to prevent flicker during rapid updates
  // React will batch rapid updates and only render the final state
  const deferredTree = useDeferredValue(tree);

  // Track the previous valid tree to prevent flashing invalid states
  const lastValidTreeRef = useRef<ComponentTree>(tree);

  // Validate deferred tree
  const validationResult = useMemo(() => validateTree(deferredTree), [deferredTree]);

  // T042: Use stable tree hash for memoization key
  const treeHash = useMemo(() => hashTree(deferredTree), [deferredTree]);

  // Update last valid tree reference
  if (validationResult.valid) {
    lastValidTreeRef.current = deferredTree;
  }

  // Show validation errors only if tree is invalid
  if (!validationResult.valid) {
    return (
      <div className={`bg-red-50 border border-red-200 rounded-lg p-4 ${className || ''}`}>
        <div className="flex items-center gap-2 text-red-700 mb-2">
          <AlertTriangle size={18} />
          <span className="font-medium">Invalid component tree</span>
        </div>
        <ul className="text-sm text-red-600 list-disc list-inside">
          {validationResult.errors.map((error, index) => (
            <li key={index}>{error}</li>
          ))}
        </ul>
      </div>
    );
  }

  // T042: Render with stable key to prevent unnecessary DOM recreation
  return (
    <div className={className} key={treeHash}>
      {/* T059: Inline error display with retry button */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-red-700">
              <AlertTriangle size={16} className="flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
            {onRetry && (
              <button
                onClick={onRetry}
                className="px-3 py-1 text-xs font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded transition-colors"
              >
                Retry
              </button>
            )}
          </div>
        </div>
      )}
      <ComponentRenderer node={deferredTree} depth={0} context={context} onAction={onAction} />
    </div>
  );
}

export const CanvasRenderer = memo(CanvasRendererBase);
